'use client'

import { useId, useRef, useSyncExternalStore } from 'react'
import { getTokensConfig, getTokens, getResolvedValue } from '@systhemaui/core'

import clsx from 'clsx'
import Link from 'next/link'

import { Swiper, SwiperSlide } from 'swiper/react'
import { FreeMode, Navigation, Pagination, Mousewheel } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper/types'

import type { SectionProps } from '@systhemaui/next'
import { Section, Paragraph, Image, Chip, ChipTitle, ChipIcon } from '@systhemaui/next'
import { Stack } from '@systhemaui/react'

import { getSysthemaConfigSync } from '@systhemaui/core'

const { breakpoints } = getTokensConfig() as { breakpoints: Record<string, string> }
const { responsiveSizing } = getTokens()

// Get transition classes - these may differ between server/client, so we apply them after hydration
const emptySubscribe = () => () => {}
function getTransitionClasses() {
  const config = getSysthemaConfigSync()
  return typeof config.packages?.react === 'object' && config.packages?.react?.enabled
    ? config.packages?.react?.transitionClasses
    : undefined
}
const clientTransitionClasses = typeof window !== 'undefined' ? getTransitionClasses() : undefined

import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'

export interface StreamingRowItem {
  title: string
  slug: string
  imageUrl: string
  description?: SerializedEditorState
}

type StreamingRowProps = Omit<SectionProps, 'children'> & {
  title?: string
  chipLabel?: string
  chipHref?: string
  chipNewTab?: boolean
  items: StreamingRowItem[]
}

const StreamingRowClient = (props: StreamingRowProps) => {
  const {
    theme = 'default',
    layoutBackground = 'base',
    items: originalItems,
    title,
    chipLabel,
    chipHref,
    chipNewTab,
    className,
    ...rest
  } = props

  const items = originalItems
  const id = useId()
  const streamingRowId = `${id.replace(/[^a-zA-Z0-9]/g, '')}`

  const navigationPrevRef = useRef<HTMLButtonElement>(null)
  const navigationNextRef = useRef<HTMLButtonElement>(null)
  const transitionClasses = useSyncExternalStore(
    emptySubscribe,
    () => clientTransitionClasses,
    () => undefined,
  )

  const handleSwiper = (swiper: SwiperType) => {
    // Use rAF to ensure navigation button refs are populated
    requestAnimationFrame(() => {
      if (!swiper.params || swiper.destroyed) return
      if (navigationPrevRef.current && navigationNextRef.current) {
        if (swiper.params.navigation && typeof swiper.params.navigation !== 'boolean') {
          swiper.params.navigation.prevEl = navigationPrevRef.current
          swiper.params.navigation.nextEl = navigationNextRef.current
          swiper.navigation.init()
          swiper.navigation.update()
        }
      }
    })
  }

  return (
    <Section
      theme={theme}
      layoutBackground={layoutBackground}
      className={clsx('streaming-row group/streaming-row relative', className)}
      {...rest}
    >
      {(title || chipHref) && (
        <Stack
          direction="row"
          justifyContent="between"
          alignItems="center"
          disableAnimation
          className="mb-4"
        >
          {title && (
            <Paragraph type="label" disableAnimation className="grow">
              {title}
            </Paragraph>
          )}
          {chipHref && (
            <Chip.a
              href={chipHref}
              target={chipNewTab ? '_blank' : undefined}
              rel={chipNewTab ? 'noopener noreferrer' : undefined}
            >
              {chipLabel && <ChipTitle>{chipLabel}</ChipTitle>}
              <ChipIcon.span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 -960 960 960"
                  fill="currentColor"
                >
                  <path d="M647-440H160v-80h487L423-744l57-56 320 320-320 320-57-56 224-224Z" />
                </svg>
              </ChipIcon.span>
            </Chip.a>
          )}
        </Stack>
      )}
      <Swiper
        id={streamingRowId}
        className="peer/swiper"
        modules={[FreeMode, Navigation, Pagination, Mousewheel]}
        slidesPerView={1.5}
        spaceBetween={parseInt(
          (getResolvedValue(responsiveSizing, 'gallery.slidesGapX', 'sm') as string) ?? '24',
        )}
        loop={false}
        // grabCursor={true}
        // autoHeight={true}
        navigation={true}
        pagination={false}
        freeMode={{
          enabled: true,
          momentum: true,
          momentumRatio: 0.5,
          momentumVelocityRatio: 0.5,
          sticky: true,
        }}
        touchEventsTarget="container"
        mousewheel={{
          forceToAxis: true,
          thresholdDelta: 100,
          releaseOnEdges: true,
        }}
        breakpoints={{
          [parseInt(breakpoints?.md as string) ?? 640]: {
            slidesPerView: 2,
            spaceBetween: parseInt(
              (getResolvedValue(responsiveSizing, 'gallery.slidesGapX', 'md') as string) ?? '24',
            ),
          },
          768: {
            slidesPerView: 3,
            spaceBetween: parseInt(
              (getResolvedValue(responsiveSizing, 'gallery.slidesGapX', 'md') as string) ?? '24',
            ),
          },
          [parseInt(breakpoints?.lg as string) ?? 1192]: {
            slidesPerView: 4,
            spaceBetween: parseInt(
              (getResolvedValue(responsiveSizing, 'gallery.slidesGapX', 'lg') as string) ?? '24',
            ),
          },
          // todo: temporarily disabled until we have enough content - might be reactivated later or removed completely
          // [parseInt(breakpoints?.xl as string) ?? 1366]: {
          //   slidesPerView: 5,
          //   spaceBetween: parseInt(
          //     (getResolvedValue(responsiveSizing, 'gallery.slidesGapX', 'lg') as string) ?? '24',
          //   ),
          // },
          // [parseInt(breakpoints?.['2xl'] as string) ?? 1728]: {
          //   slidesPerView: 6,
          //   spaceBetween: parseInt(
          //     (getResolvedValue(responsiveSizing, 'gallery.slidesGapX', 'lg') as string) ?? '24',
          //   ),
          // },
        }}
        onSwiper={handleSwiper}
      >
        {items?.map((item) => (
          <SwiperSlide key={item.slug}>
            <Link
              href={`/musor/${item.slug}`}
              className="streaming-card image-skeleton group/streaming-card ease-out-cubic relative isolate block aspect-16/9 overflow-hidden rounded-none border border-transparent transition-colors duration-300"
            >
              <Image
                className="absolute inset-0 aspect-auto h-full w-full transition-opacity duration-300 group-hover/streaming-card:opacity-50"
                src={item.imageUrl}
                alt={item.title}
                objectFit="cover"
                loading="lazy"
                decoding="async"
                fetchPriority="low"
                sizes="
                  (max-width: 640px) 50vw,
                  (max-width: 1192px) 25vw,
                  20vw
                "
                disableAnimation
              />
              <span className="sr-only!">{item.title}</span>
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>
      <div className="ease-out-cubic absolute bottom-(--section-padding-y) left-0 z-10 grid h-[calc(100%-var(--section-padding-y)-40px)] w-48 place-items-center bg-linear-to-r from-blue-950 to-transparent opacity-0 transition-opacity duration-300 group-hover/streaming-row:opacity-100 hover:opacity-100 has-disabled:pointer-events-none has-disabled:opacity-0! max-md:hidden">
        <button
          ref={navigationPrevRef}
          className={clsx(
            'streaming-row-navigation-button button-primary cursor-pointer disabled:cursor-not-allowed disabled:bg-(--color-button-primary-normal-background)',
            transitionClasses,
          )}
          aria-controls={`${streamingRowId}-swiper`}
          aria-label="Previous Slide"
          tabIndex={0}
        >
          <svg className="button-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
            <path
              fill="currentColor"
              d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H109.3l105.3-105.4c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"
            />
          </svg>
          <span className="sr-only!">Previous</span>
        </button>
      </div>
      <div className="ease-out-cubic absolute right-0 bottom-(--section-padding-y) z-10 grid h-[calc(100%-var(--section-padding-y)-40px)] w-48 place-items-center bg-linear-to-l from-blue-950 to-transparent opacity-0 transition-opacity duration-300 group-hover/streaming-row:opacity-100 hover:opacity-100 has-disabled:pointer-events-none has-disabled:opacity-0! max-md:hidden">
        <button
          ref={navigationNextRef}
          className={clsx(
            'streaming-row-navigation-button button-primary cursor-pointer disabled:cursor-not-allowed disabled:bg-(--color-button-primary-normal-background)',
            transitionClasses,
          )}
          aria-controls={`${streamingRowId}-swiper`}
          aria-label="Next Slide"
          tabIndex={0}
        >
          <span className="sr-only!">Next</span>
          <svg className="button-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
            <path
              fill="currentColor"
              d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224H32c-17.7 0-32 14.3-32 32s14.3 32 32 32h306.7L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z"
            />
          </svg>
        </button>
      </div>
    </Section>
  )
}

export default StreamingRowClient
